'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check } from 'lucide-react';
import { AdaptiveDialog } from '@/shared/ui/AdaptiveDialog';
import { cn } from '@/shared/lib/cn';
import type { DesignLineContext } from '../api/design-line-service';

/** Where a new estimate goes: an existing line, or a new one. */
export type EstimateLinePlacement =
  | { design_line_id: string }
  | { new_design_line: true };

interface EstimateLineChoiceDialogProps {
  open: boolean;
  onClose: () => void;
  context: DesignLineContext;
  isSubmitting: boolean;
  onConfirm: (placement: EstimateLinePlacement) => void;
}

const NEW_LINE = 'new';

/**
 * "Which estimate to update?" — shown before generating when the building
 * already has estimates. One row per line ("Update estimate «Brick»"), then
 * "New estimate". Preselects the design's own line, else the API
 * `suggested_design_line_id`, else "New estimate". Picking a row only selects it; the Generate button decides.
 */
export function EstimateLineChoiceDialog({
  open,
  onClose,
  context,
  isSubmitting,
  onConfirm,
}: EstimateLineChoiceDialogProps) {
  const t = useTranslations('common.designLine');
  const tEstimate = useTranslations('common.estimate');
  const [selected, setSelected] = useState(
    context.design_line_id ?? context.suggested_design_line_id ?? NEW_LINE
  );

  const options = [
    ...context.lines.map((line) => ({
      id: line.id,
      label: t('updateEstimateNamed', { title: line.display_title }),
    })),
    { id: NEW_LINE, label: t('newEstimate') },
  ];

  return (
    <AdaptiveDialog
      open={open}
      onClose={onClose}
      title={t('chooseEstimateTitle')}
      saveLabel={tEstimate('generate')}
      isSaving={isSubmitting}
      onSave={() =>
        onConfirm(
          selected === NEW_LINE
            ? { new_design_line: true }
            : { design_line_id: selected }
        )
      }
    >
      <div className="space-y-2">
        {options.map((option) => {
          const isSelected = option.id === selected;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => setSelected(option.id)}
              disabled={isSubmitting}
              className={cn(
                'w-full flex items-center justify-between gap-2 p-4 rounded-lg border-2 transition-colors text-start',
                isSelected ? 'border-primary bg-primary/5' : 'border-border',
                !isSubmitting && 'hover:bg-muted/50'
              )}
            >
              <span className="text-list-item-subtitle">{option.label}</span>
              {isSelected && (
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>
    </AdaptiveDialog>
  );
}
